/**
 * AI Recruit - Idle Timeout Handler Component
 * Handles user idle timeout with warning dialog and automatic logout
 */

import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useIdleTimeout } from '../../hooks/useIdleTimeout'
import { useAuth } from '../../hooks/useAuth'
import { ExclamationTriangleIcon, ClockIcon } from '@heroicons/react/24/outline'
import { IDLE_TIMEOUT_CONFIG } from '../../config/idleTimeout'

interface IdleTimeoutHandlerProps {
  timeoutMinutes?: number
  warningMinutes?: number
}

const IdleTimeoutHandler: React.FC<IdleTimeoutHandlerProps> = ({
  timeoutMinutes = IDLE_TIMEOUT_CONFIG.TIMEOUT_MINUTES,
  warningMinutes = IDLE_TIMEOUT_CONFIG.WARNING_MINUTES
}) => {
  const [showWarning, setShowWarning] = useState(false)
  const [timeLeft, setTimeLeft] = useState(warningMinutes * 60)
  const { logout, refreshToken, isAuthenticated } = useAuth()
  const countdownRef = useRef<NodeJS.Timeout | null>(null)
  const logoutCalledRef = useRef(false)

  const stopCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current)
      countdownRef.current = null
    }
  }, [])

  // Stable callbacks — memoized so useIdleTimeout's effect doesn't re-run on every render
  const handleIdle = useCallback(() => {
    if (!isAuthenticated) return
    if (IDLE_TIMEOUT_CONFIG.ENABLE_CONSOLE_LOGS) {
      console.log('User is idle, showing warning dialog')
    }
    logoutCalledRef.current = false
    setShowWarning(true)
    setTimeLeft(warningMinutes * 60)
  }, [isAuthenticated, warningMinutes])

  const handleActive = useCallback(() => {
    if (IDLE_TIMEOUT_CONFIG.ENABLE_CONSOLE_LOGS) {
      console.log('User is active, hiding warning dialog')
    }
    stopCountdown()
    setShowWarning(false)
    setTimeLeft(warningMinutes * 60)
    // Proactively refresh the token so the session is extended immediately
    // rather than waiting for the next API call to trigger a 401
    refreshToken()
  }, [warningMinutes, stopCountdown, refreshToken])

  // Start/stop the countdown interval when the warning appears or disappears
  useEffect(() => {
    if (!showWarning) return

    countdownRef.current = setInterval(() => {
      setTimeLeft(prev => Math.max(0, prev - 1))
    }, 1000)

    return stopCountdown
  }, [showWarning, stopCountdown])

  // Trigger logout when countdown reaches zero — side-effect kept outside setState
  useEffect(() => {
    if (!showWarning || timeLeft > 0 || logoutCalledRef.current) return
    logoutCalledRef.current = true
    if (IDLE_TIMEOUT_CONFIG.ENABLE_CONSOLE_LOGS) {
      console.log('Logging out due to idle timeout')
    }
    stopCountdown()
    setShowWarning(false)
    logout()
  }, [showWarning, timeLeft, logout, stopCountdown])

  const handleLogout = useCallback(async () => {
    if (IDLE_TIMEOUT_CONFIG.ENABLE_CONSOLE_LOGS) {
      console.log('User manually logged out from idle warning')
    }
    stopCountdown()
    setShowWarning(false)
    await logout()
  }, [logout, stopCountdown])

  const handleStayLoggedIn = useCallback(() => {
    if (IDLE_TIMEOUT_CONFIG.ENABLE_CONSOLE_LOGS) {
      console.log('User chose to stay logged in')
    }
    stopCountdown()
    setShowWarning(false)
    setTimeLeft(warningMinutes * 60)
    // Refresh token on explicit user confirmation as well
    refreshToken()
  }, [warningMinutes, stopCountdown, refreshToken])

  // Use the idle timeout hook — stable callbacks prevent effect from re-running on every render
  useIdleTimeout({
    timeout: (timeoutMinutes - warningMinutes) * 60 * 1000,
    onIdle: handleIdle,
    onActive: handleActive,
    events: IDLE_TIMEOUT_CONFIG.RESET_EVENTS
  })

  // Cleanup on unmount
  useEffect(() => {
    return stopCountdown
  }, [stopCountdown])

  // Don't render anything if not authenticated or warning not shown
  if (!isAuthenticated || !showWarning) {
    return null
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="flex-shrink-0">
            <ExclamationTriangleIcon className="h-8 w-8 text-yellow-500" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Session Timeout Warning
            </h3>
            <p className="text-sm text-gray-500">
              You will be logged out due to inactivity
            </p>
          </div>
        </div>

        <div className="mb-6">
          <div className="flex items-center space-x-2 mb-2">
            <ClockIcon className="h-5 w-5 text-red-500" />
            <span className="text-sm font-medium text-gray-700">
              Time remaining: {formatTime(timeLeft)}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-red-500 h-2 rounded-full transition-all duration-1000"
              style={{ 
                width: `${(timeLeft / (warningMinutes * 60)) * 100}%` 
              }}
            />
          </div>
        </div>

        <div className="flex space-x-3">
          <button
            onClick={handleStayLoggedIn}
            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
          >
            <span>Stay Logged In</span>
          </button>
          <button
            onClick={handleLogout}
            className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors"
          >
            Logout Now
          </button>
        </div>

        <p className="text-xs text-gray-500 mt-4 text-center">
          Click "Stay Logged In" to continue your session, or you will be automatically logged out.
        </p>
      </div>
    </div>
  )
}

export default IdleTimeoutHandler
